import { Button, SearchBox, SelectTabData, SelectTabEvent, Switch, Tab, TabList, makeStyles, tokens } from '@fluentui/react-components'
import { t } from '../../i18n/i18n'
import { Page } from '../Page'
import { Title } from '../../components/common/texts/Title'
import { CustomDemandTable } from './CustomDemandTable'
import { DataContext } from '../../App'
import { QuestionTable } from '../Question/QuestionTable'
import { excelExporter, ExportStatus } from '../../helpers/excelHelper'
import { DialogExportingList } from './DialogExportingList'
import { ArchiveRegular, ArrowExportFilled, BookQuestionMarkRegular, BookRegular, ArrowForwardRegular, CubeQuickRegular } from '@fluentui/react-icons'
import { onDemandMoveClick } from '../../helpers/demandHelper'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useBoolean } from '@fluentui/react-hooks'
import { toggleExpandAllDemands } from '../../helpers/dndHelper'
import { DemandLevel } from '../../api/schemas/schema'
import { DemandSearchResultList } from './DemandSearchResultList'
import { DemandSearchSummary } from './DemandSearchSummary'
import { MIN_SEARCH_LENGTH, searchDemandsAndQuestions } from '../../helpers/demandSearchHelper'
import { normalizeForSearch } from '../../helpers/searchHelper'
import { useDebouncedValue } from '../../helpers/debounceHelper'
import { Text } from '../../components/common/texts/Text'

const SEARCH_DEBOUNCE_MS = 275
const useStyles = makeStyles({
   flexColumn: {
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'column',
   },
   stickyWrapper: {
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      backgroundColor: tokens.colorNeutralBackground1,
      padding: '10px',
   },
   listNavContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '20px',
      marginLeft: '10px',
   },
   flexRow: {
      display: 'flex',
   },
   iconMargin: {
      marginRight: tokens.spacingHorizontalSNudge,
   },
   separateTabItems: {
      display: 'flex',
      justifyContent: 'space-between',
   },
   searchBox: {
      width: '280px',
      maxWidth: '280px',
      marginRight: tokens.spacingHorizontalM,
   },
   searchArea: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalXXS,
   },
   searchHint: {
      color: tokens.colorNeutralForeground3,
   },
})

export const ComplianceOfDemands = () => {
   const classes = useStyles()

   const dataContext = useContext(DataContext)
   const [exportingStatus, setExportingStatus] = useState<ExportStatus>(null)
   const { selectedTab, moveDemands, selectedDemands, demands, demandSearchQuery } = dataContext.state
   const [expandAllDemands, setExpandDemands] = useState<boolean>(false)
   const [dialogOpen, { setTrue: moveDemandModalOpen, setFalse: moveDemandModalClosed }] = useBoolean(false)
   // Seeded from global state, which is what makes the search survive navigating into a demand and back.
   const [searchInput, setSearchInput] = useState<string>(demandSearchQuery)
   const debouncedInput = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS)
   const isSearching = !!demandSearchQuery
   const trimmedInput = searchInput.trim()
   const queryTooShort = trimmedInput.length > 0 && trimmedInput.length < MIN_SEARCH_LENGTH
   const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
      dataContext.setRootState((prev) => ({ ...prev, selectedTab: data.value.toString() }))
   }
   const handleSwitchChange = () => {
      const isExpanded = !expandAllDemands
      setExpandDemands(isExpanded)
      toggleExpandAllDemands(dataContext, isExpanded)
   }

   /** Bails out with `prev` when unchanged: this context is root-level, so every write re-renders all of it. */
   const commitSearch = (value: string) => {
      const query = value.trim()
      dataContext.setRootState((prev) => (prev.demandSearchQuery === query ? prev : { ...prev, demandSearchQuery: query }))
   }

   // Stable identity, or DemandSearchResultList's memo never holds.
   const clearSearch = useCallback(() => {
      setSearchInput('')
      dataContext.setRootState((prev) => (prev.demandSearchQuery === '' ? prev : { ...prev, demandSearchQuery: '' }))
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      const trimmed = debouncedInput.trim()
      commitSearch(trimmed.length < MIN_SEARCH_LENGTH ? '' : trimmed)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [debouncedInput])

   const hits = useMemo(
      () => (demands && demandSearchQuery ? searchDemandsAndQuestions(demands, demandSearchQuery) : []),
      [demands, demandSearchQuery]
   )
   const normalizedQuery = useMemo(() => normalizeForSearch(demandSearchQuery.trim()), [demandSearchQuery])
   const demandHitCount = hits.filter((hit) => hit.kind === 'demand').length

   return (
      <Page
         header={<Title as="h2">{t('DemandQuestionList')}</Title>}
         subheader={
            <div className={classes.flexColumn}>
               <div className={classes.separateTabItems}>
                  <TabList selectedValue={selectedTab} onTabSelect={onTabSelect} disabled={isSearching}>
                     <Tab value="activeDemands" icon={<BookRegular />}>
                        {t('ActiveDemands')}
                     </Tab>
                     <Tab value="activeQuestions" icon={<BookQuestionMarkRegular />}>
                        {t('ActiveQuestions')}
                     </Tab>
                     <Tab value="archivedDemands" icon={<ArchiveRegular />}>
                        {t('ArchivedDemands')}
                     </Tab>
                  </TabList>
                  {/* The exporter ignores the query, so exporting while searching yields the full list. */}
                  <Button
                     disabled={exportingStatus !== null || isSearching}
                     title={isSearching ? t('ExportDisabledDuringSearch') : undefined}
                     onClick={async () => await excelExporter(dataContext, setExportingStatus)}
                     appearance="subtle"
                     icon={<ArrowExportFilled />}
                  >
                     {t('ExportDemandsAndQuestionsToExcel')}
                  </Button>
               </div>

               <div className={classes.listNavContainer}>
                  <div className={classes.flexRow}>
                     <div className={classes.searchArea}>
                        <SearchBox
                           className={classes.searchBox}
                           placeholder={t('SearchDemandsAndQuestions')}
                           value={searchInput}
                           disabled={moveDemands}
                           title={moveDemands ? t('SearchDisabledDuringFastMove') : undefined}
                           onChange={(e, data) => {
                              setSearchInput(data.value)
                              // The dismiss cross fires onChange empty; clear at once, not after the debounce.
                              if (!data.value) commitSearch('')
                           }}
                           onKeyDown={(e) => {
                              // Enter skips the debounce, but still respects the minimum length.
                              if (e.key === 'Enter' && trimmedInput.length >= MIN_SEARCH_LENGTH) commitSearch(searchInput)
                              if (e.key === 'Escape') clearSearch()
                           }}
                        />
                        {queryTooShort && (
                           <Text as="caption" className={classes.searchHint}>
                              {t('SearchMinLengthHint')}
                           </Text>
                        )}
                     </div>
                     {selectedTab === 'activeDemands' && (
                        <>
                           {moveDemands && (
                              <Button
                                 appearance="subtle"
                                 icon={<ArrowForwardRegular />}
                                 disabled={
                                    isSearching ||
                                    !selectedDemands.length ||
                                    (selectedDemands && !selectedDemands.find((x) => x.level === DemandLevel.Two || x.level === DemandLevel.Three))
                                 }
                                 onClick={moveDemandModalOpen}
                              >
                                 {t('MoveTo')}
                              </Button>
                           )}
                           <Button
                              appearance="subtle"
                              icon={<CubeQuickRegular />}
                              // Also blocks on a not-yet-committed query, so a fast move can't start in the debounce window before isSearching flips true.
                              disabled={isSearching || !!trimmedInput}
                              title={!moveDemands && trimmedInput ? t('FastMoveDisabledDuringSearch') : undefined}
                              onClick={() => onDemandMoveClick(dataContext)}
                           >
                              {t(`${moveDemands ? 'AbortFastMove' : 'FastMoveDemand'}`)}
                           </Button>
                        </>
                     )}
                  </div>
                  <Switch
                     label={t(expandAllDemands ? 'MiniMizeAll' : 'ExpandAll')}
                     disabled={!demands || isSearching}
                     checked={expandAllDemands}
                     onChange={handleSwitchChange}
                  />
               </div>
               <DemandSearchSummary
                  isSearching={isSearching}
                  loading={!demands}
                  demandCount={demandHitCount}
                  questionCount={hits.length - demandHitCount}
                  onClear={clearSearch}
               />
               <DialogExportingList exportingStatus={exportingStatus} />
            </div>
         }
         stickySubHeader
      >
         {isSearching ? (
            <DemandSearchResultList hits={hits} normalizedQuery={normalizedQuery} loading={!demands} onClear={clearSearch} />
         ) : selectedTab === 'activeQuestions' ? (
            <QuestionTable />
         ) : (
            <CustomDemandTable dialogOpen={dialogOpen} expandAllDemands={expandAllDemands} moveDemandModalClosed={moveDemandModalClosed} />
         )}
      </Page>
   )
}
